import { IconName, IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import React, { forwardRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Box, { BoxProps } from 'ui-box';
import { Icon } from './Icon';
import { getColorFromIntent, Intents } from './Intents';
import { Spinner } from './Spinner';

export interface IButtonProps extends BoxProps<any> {
  iconLeft?: ReactNode | IconName;
  iconRight?: ReactNode | IconName;
  minimal?: boolean;
  primary?: boolean;
  intent?: Intents;
  small?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  linkTo?: string;
  onClick?: (e: any) => any | Promise<any>;
}

export const Button: React.FC<IButtonProps> = forwardRef(
  (
    {
      iconLeft,
      iconRight,
      children,
      minimal,
      primary,
      intent = 'none',
      height = 32,
      small = false,
      disabled = false,
      isLoading: isLoadingOuter = false,
      className,
      linkTo,
      onClick,
      ...props
    },
    ref
  ) => {
    const isIconString = (o: any) =>
      typeof o === 'string' &&
      Object.values(IconNames).indexOf(o as IconName) > -1;

    const [isLoadingInner, setIsLoadingInner] = React.useState(false);

    const isLoading = isLoadingOuter || isLoadingInner;

    return (
      <Box
        is={linkTo ? Link : props.is === 'a' ? 'a' : 'button'}
        {...{
          to: linkTo,
          disabled: linkTo ? undefined : isLoading || disabled,
          display: linkTo ? 'inline-flex' : undefined,
        }}
        height={small ? 24 : height}
        minHeight={small ? 24 : height}
        className={classNames(
          'button',
          intent,
          minimal && 'minimal',
          primary && 'primary',
          (isLoading || disabled) && 'disabled',
          className
        )}
        {...props}
        ref={ref}
        tabIndex={props.tabIndex}
        position="relative"
        onClick={async (e: any) => {
          if (!onClick) return;
          setIsLoadingInner(true);
          Promise.resolve(onClick(e)).finally(() => setIsLoadingInner(false));
        }}
      >
        {isLoading && (
          <Spinner
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            size={16}
          />
        )}

        {iconLeft && isIconString(iconLeft) ? (
          <Icon
            icon={iconLeft as any}
            height={16}
            opacity={isLoading ? 0 : 1}
            color={primary ? 'white' : getColorFromIntent(intent)}
          />
        ) : (
          iconLeft
        )}
        {children && (
          <Box
            is="span"
            marginLeft={!iconLeft ? (!iconRight ? 'auto' : 0) : 8}
            marginRight={!iconRight ? 'auto' : 8}
            opacity={isLoading ? 0 : 1}
            whiteSpace="nowrap"
          >
            {children}
          </Box>
        )}
        {iconRight && isIconString(iconRight) ? (
          <Icon
            icon={iconRight as any}
            height={16}
            opacity={isLoading ? 0 : 1}
            color={primary ? 'white' : getColorFromIntent(intent)}
          />
        ) : (
          iconRight
        )}
      </Box>
    );
  }
);
